import { Box, Pagination, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import {
  Bar,
  BarChart as RBarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface CostBarChartProps {
  title: string;
  data: { name: string; value: number }[];
  color?: string;
  unit?: 'currency' | 'count' | 'percentage';
  isLoading?: boolean;
  hasPagination?: boolean;
  page?: number;
  pageSize?: number;
  setPage?: React.Dispatch<React.SetStateAction<number>>;
  totalCount?: number;
}

const CostBarChart: React.FC<CostBarChartProps> = ({
  color = '#5C8CE1',
  data,
  hasPagination = false,
  isLoading = false,
  page = 1,
  pageSize = 10,
  setPage,
  title,
  totalCount,
  unit = 'currency',
}) => {
  const formatter = (value: number) => {
    if (unit === 'currency') return `$${value.toLocaleString()}`;
    if (unit === 'percentage') return `${value.toFixed(1)}%`;

    return value.toLocaleString();
  };

  const theme = useTheme();

  return (
    <Box sx={{ backgroundColor: '#fff', borderRadius: 2, p: 3, boxShadow: 1 }}>
      <Typography mb={2} variant="h6">
        {title}
      </Typography>
      <ResponsiveContainer height={300} width="100%">
        {isLoading ? (
          <Skeleton height={300} variant="rectangular" />
        ) : data.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" width="100%">
            <Typography variant="caption">No Data available</Typography>
          </Stack>
        ) : (
          <RBarChart
            data={data}
            layout="vertical"
            margin={{ top: 10, right: 20, left: 100, bottom: 10 }}
          >
            <defs>
              <linearGradient id="cost-bar-gradient" x1="0" x2="0" y1="1" y2="0">
                <stop offset="0%" stopColor={color} stopOpacity={0.4} />
                <stop offset="100%" stopColor={color} stopOpacity={1} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              fontSize={'13px'}
              fontWeight={400}
              stroke={'rgba(32, 39, 35, 0.25)'}
              type="number"
            />
            <YAxis
              dataKey="name"
              fontSize={'13px'}
              fontWeight={400}
              stroke={theme.colors.functional.text.primary}
              type="category"
              width={120}
            />
            <Tooltip formatter={formatter} />
            <Bar dataKey="value" fill={color}>
              {data.map((_, index) => (
                <Cell fill={color} key={`cell-${index}`} />
              ))}
            </Bar>
          </RBarChart>
        )}
      </ResponsiveContainer>

      {hasPagination && totalCount !== undefined && (
        <Stack alignItems="center" mt={2}>
          <Pagination
            color="primary"
            count={Math.ceil(totalCount / pageSize)}
            onChange={(_, value) => setPage?.(value)}
            page={page}
            shape="rounded"
            size="small"
          />
        </Stack>
      )}
    </Box>
  );
};

export default CostBarChart;
